/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.data.repositoryImpl.cauce

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import studio.oddpods.cauce.core.data.repository.cauce.DashboardRepository
import studio.oddpods.cauce.core.data.repository.cauce.PatientRepository
import studio.oddpods.cauce.core.data.util.BusinessError
import studio.oddpods.cauce.core.data.util.safeCall
import studio.oddpods.cauce.core.data.util.safeThrow
import studio.oddpods.cauce.core.model.patient.CreatePatientRequest
import studio.oddpods.cauce.core.model.patient.Patient

class PatientRepositoryImpl(
    private val supabase: SupabaseClient,
    private val dashboardRepository: DashboardRepository,
) : PatientRepository {

    private val allPatients = MutableStateFlow<List<Patient>?>(null)

    override fun getPatients(activeOnly: Boolean): Flow<List<Patient>> =
        allPatients
            .onStart { if (allPatients.value == null) fetchAll() }
            .filterNotNull()
            .map { list -> if (activeOnly) list.filter { it.active } else list }

    override fun observePatient(patientId: String): Flow<Patient?> =
        allPatients
            .onStart { if (allPatients.value == null) fetchAll() }
            .filterNotNull()
            .map { list -> list.firstOrNull { it.id == patientId } }

    override suspend fun getPatient(patientId: String): Result<Patient> = safeCall {
        allPatients.value?.firstOrNull { it.id == patientId }
            ?: supabase.postgrest.from("patients").select {
                filter { eq("id", patientId) }
            }.decodeSingle<Patient>()
    }

    override suspend fun createPatient(request: CreatePatientRequest): Result<Patient> = safeCall {
        val withinLimits = supabase.postgrest.rpc(
            "check_tier_limits",
            buildJsonObject { put("resource_type", "patients") },
        ).decodeAs<Boolean>()

        if (!withinLimits) throw BusinessError.LimitReached("patients")

        val inserted = supabase.postgrest.from("patients").insert(request) {
            select()
        }.decodeSingle<Patient>()
        fetchAll()
        inserted
    }

    override suspend fun updatePatient(patientId: String, request: CreatePatientRequest): Result<Patient> = safeCall {
        val updated = supabase.postgrest.from("patients").update({
            set("name", request.name)
            set("phone", request.phone)
            set("email", request.email)
            set("default_fee", request.defaultFee)
            set("notes", request.notes)
            set("reminders_paused", request.remindersPaused)
        }) {
            filter { eq("id", patientId) }
            select()
        }.decodeSingle<Patient>()
        fetchAll()
        updated
    }

    override suspend fun deactivatePatient(patientId: String): Result<Unit> = safeCall {
        supabase.postgrest.from("patients").update({
            set("active", false)
        }) {
            filter { eq("id", patientId) }
        }
        fetchAll()
        dashboardRepository.refresh().getOrNull()
        Unit
    }

    private suspend fun fetchAll() = safeThrow {
        allPatients.value = supabase.postgrest.from("patients").select {
            order("created_at", io.github.jan.supabase.postgrest.query.Order.DESCENDING)
        }.decodeList<Patient>()
    }
}
